import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    Self,
    SimpleChanges,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NgControl,
    ValidationErrors,
    Validator,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import DataSource from 'devextreme/data/data_source';
import { nameof } from '@bds/core';
import { RtRouteCode } from '@bds/railtrac-models';
import { dxMatStyle } from '@bds/smart-components';
import { RtRouteService } from '@bds/data-access';
import { BdsRouteSearchDialogComponent } from '../../../bds-route/components/bds-route-search-dialog/bds-route-search-dialog.component';
import { BdsRouteSearchDialogModel } from '../../../bds-route/components/bds-route-search-dialog/bds-route-search-dialog-model';

@Component({
    selector: 'bds-route-advanced-select',
    templateUrl: './rt-route-advanced-select.component.html',
    styleUrls: ['./rt-route-advanced-select.component.scss'],
})
export class RtRouteAdvancedSelectComponent
    implements ControlValueAccessor, Validator, OnInit, OnChanges
{
    static nextId = 0;

    routeSearchInlineButtonOptions = {
        type: 'default',
        icon: 'search',
        stylingMode: 'text',
        onClick: () => {
            this.searchForRoute();
        },
    };

    dataSource: DataSource;
    disabled: boolean;
    id = `bds-route-advanced-select-${RtRouteAdvancedSelectComponent.nextId++}`;
    readonly: boolean;
    searchExp: string[];
    selectedItem: RtRouteCode;
    val: string;
    valueExp: string;

    get style(): string {
        return dxMatStyle(this.matStyle);
    }

    get value(): string {
        return this.val;
    }

    set value(v: string) {
        if (v !== this.val) {
            this.val = v;
            this.onChangeCallback(v);
        }
    }

    @Input() dense = true;
    @Input() filter: [] = null;
    @Input() isDisabled = false;
    @Input() isReadonly = false;
    @Input() isRequired = false;
    @Input() matFloatLabel = 'always';
    @Input() matStyle: 'fill' | 'outline' | 'standard' | 'legacy' = 'fill';
    @Input() pageSize = 10;
    @Output() selectionChange: EventEmitter<RtRouteCode> = new EventEmitter();

    @Input() displayValue = function (data: RtRouteCode) {
        const code = this.routeApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtRouteCode>('routeCode'),
        ).server;
        const desc = this.routeApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtRouteCode>('routeDscr'),
        ).server;
        return data ? `${data[code]} - ${data[desc]}` : '';
    };

    constructor(
        private routeApiService: RtRouteService,
        private dialog: MatDialog,
        @Optional() @Self() public ngControl: NgControl,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit() {
        this.setControlValidators();

        this.searchExp = [
            this.routeApiService.adapter.metadata.find(
                (f) => f.client === nameof<RtRouteCode>('routeCode'),
            ).server,
            this.routeApiService.adapter.metadata.find(
                (f) => f.client === nameof<RtRouteCode>('routeDscr'),
            ).server,
        ];
        this.valueExp = this.routeApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtRouteCode>('routeCode'),
        ).server;

        this.setDataSource();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.filter) {
            this.selectedItem = null;
            this.value = null;
            this.setDataSource();
        }

        if (changes.isRequired && !changes.isRequired.firstChange) {
            // TODO: May need to clear first
            this.setControlValidators();
        }

        if (changes.isReadonly) {
            this.setDisabledState(this.isReadonly);
        }

        if (changes.isDisabled) {
            this.setDisabledState(this.isDisabled);
        }
    }

    getDisplayValue(data: RtRouteCode) {
        return this.displayValue(data);
    }

    getError(): string {
        if (this.ngControl && this.ngControl.errors) {
            if (this.ngControl.errors['required']) {
                return 'Required';
            }
        }

        return '';
    }

    onBlur() {
        this.onTouchedCallback();
    }

    onChangeCallback(event) {}

    onSelectionChanged(event) {
        if (event && (event.selectedItem || event.selectedItem === null)) {
            const selectedRoute = event.selectedItem as RtRouteCode;

            if (selectedRoute && this.value !== selectedRoute.routeCode) {
                this.selectedItem = selectedRoute;
                this.value = selectedRoute.routeCode;
                this.selectionChange.emit(selectedRoute);
            } else if (!selectedRoute) {
                this.selectedItem = null;
                this.value = null;
                this.selectionChange.emit(null);
            }
        }
    }

    onTouchedCallback() {}

    registerOnChange(fn: any): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }

    searchForRoute() {
        // Clear the Skip so that the initial rows will show on the grid
        this.setDataSource();
        const data = new BdsRouteSearchDialogModel();
        data.actionText = 'Select Route';
        data.title = 'Select Route';

        const dialogRef = this.dialog.open(BdsRouteSearchDialogComponent, {
            data: data,
        });
        dialogRef.afterClosed().subscribe((s: RtRouteCode) => {
            if (s) {
                this.selectedItem = s;
                this.value = s.routeCode;
                this.selectionChange.emit(s);
            }
        });
    }

    setDataSource() {
        this.dataSource = new DataSource({
            store: this.routeApiService.getODataStore(),
            filter: this.filter,
            paginate: true,
            pageSize: 10,
        });
    }

    setControlValidators() {
        if (this.ngControl) {
            const control = this.ngControl.control;
            const validators: ValidatorFn[] = control.validator ? [control.validator] : [];

            if (this.isRequired) {
                validators.push(Validators.required);
            }

            control.setValidators(validators);
            control.updateValueAndValidity();
        }
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    validate(c: AbstractControl): ValidationErrors {
        const validators: ValidatorFn[] = [];
        if (this.isRequired) {
            validators.push(Validators.required);
        }

        return validators;
    }

    writeValue(value: string): void {
        if (value !== undefined && value !== this.val) {
            this.value = value;
        }
    }
}
